import { Controller } from "@hotwired/stimulus"
import { post } from "api"

function pluralize(count, singular, plural) {
  return count === 1 ? singular : plural
}

const PREVIEW_LIMIT = 100

export default class extends Controller {
  static targets = ["downloadCsv", "editor", "results", "rows", "runButton", "sqlBodyField", "status", "timing"]
  static values = { executeUrl: String }

  syncSqlToForm() {
    if (this.hasSqlBodyFieldTarget) {
      this.sqlBodyFieldTarget.value = this.editorTarget.value
    }
  }

  onSaveComplete = (event) => {
    if (event.detail.success) {
      this.element.querySelector('dialog [data-action*="dialog#close"]')?.click()
    }
  }

  async run() {
    const sql = this.editorTarget.value.trim()
    if (!sql) {
      this.statusTarget.textContent = "Please enter a SQL query"
      return
    }

    this.runButtonTarget.disabled = true
    this.statusTarget.textContent = "Running..."
    this.statusTarget.className = "text-sm text-gray-500"
    this.lastResult = null

    try {
      const data = await post(this.executeUrlValue, { sql_body: sql })
      this.resultsTarget.innerHTML = ""

      if (!data.ok) {
        this.statusTarget.textContent = ""
        const message = data.error || data.text || "Request failed"
        this.resultsTarget.innerHTML = `<div class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">${this.escapeHtml(message)}</div>`
        return
      }

      this.rowsTarget.parentElement.hidden = false
      this.rowsTarget.textContent = `${data.row_count} ${pluralize(data.row_count, "row", "rows")}`
      this.timingTarget.textContent = `${data.execution_time_ms}ms`

      this.statusTarget.textContent = `${data.row_count} rows in ${data.execution_time_ms}ms`
      this.statusTarget.className = "text-sm text-green-600"

      if (data.columns.length === 0) {
        this.resultsTarget.innerHTML = `<p class="text-sm text-gray-500">Query returned no columns</p>`
        return
      }

      this.lastResult = data
      this.renderTable(false)
      if (this.hasDownloadCsvTarget) this.downloadCsvTarget.hidden = false
    } catch (error) {
      this.statusTarget.textContent = ""
      this.resultsTarget.innerHTML = `<div class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">Network error: ${this.escapeHtml(error.message)}</div>`
    } finally {
      this.runButtonTarget.disabled = false
    }
  }

  showAllRows() {
    this.renderTable(true)
  }

  renderTable(showAll) {
    const { columns, rows } = this.lastResult
    const visibleRows = showAll ? rows : rows.slice(0, PREVIEW_LIMIT)
    const remaining = rows.length - PREVIEW_LIMIT

    let html = this.buildTable(columns, visibleRows)

    if (!showAll && remaining > 0) {
      html += `<button type="button"
        class="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
        data-action="sql-query-runner#showAllRows">Show all ${rows.length.toLocaleString()} rows (${remaining.toLocaleString()} more)</button>`
    }

    this.resultsTarget.innerHTML = html
  }

  buildTable(columns, rows) {
    const headerCells = columns.map(col =>
      `<th class="text-left py-2 px-3 border font-medium text-gray-700 bg-gray-50 text-xs whitespace-nowrap">${this.escapeHtml(col)}</th>`
    ).join("")

    const bodyRows = rows.map(row => {
      const cells = columns.map(col => {
        const val = row[col]
        let display
        if (val === null) {
          display = '<span class="text-gray-400 italic">NULL</span>'
        } else if (col.startsWith("link_to") && this.safeLinkHref(val)) {
          let a = document.createElement("a")
          a.href = this.safeLinkHref(val)
          a.innerText = String(val)
          a.target = "_blank"
          a.rel = "noopener noreferrer"
          a.classList.add("link", "underline")
          display = a.outerHTML
        } else {
          display = this.escapeHtml(String(val))
        }
        return `<td class="py-1.5 px-3 text-sm border border-gray-100 whitespace-nowrap">${display}</td>`
      }).join("")
      return `<tr class="transition-all hover:bg-gray-50 bg-white">${cells}</tr>`
    }).join("")

    return `
      <div class="overflow-x-auto border border-gray-200 rounded-lg">
        <table class="w-full">
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </div>
    `
  }

  downloadCsv(e) {
    if (!this.lastResult) return

    const { columns, rows } = this.lastResult

    const csvRows = [
      columns.map(col => this.csvEscape(col)).join(","),
      ...rows.map(row =>
        columns.map(col => {
          const val = row[col]
          return val === null ? "" : this.csvEscape(String(val))
        }).join(",")
      )
    ]

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = e.currentTarget.dataset.filename || "query_results.csv"
    a.click()
    URL.revokeObjectURL(url)
  }

  csvEscape(value) {
    if (/[",\n\r]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Returns a safe href for a link_to* cell, or null if the value isn't an
  // http(s)/mailto URL. Blocks javascript:, data:, and other dangerous schemes.
  safeLinkHref(value) {
    try {
      const url = new URL(String(value).trim(), window.location.origin)
      return ["http:", "https:", "mailto:"].includes(url.protocol) ? url.href : null
    } catch {
      return null
    }
  }
}
